import type { ReactNode } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TableChartIcon from '@mui/icons-material/TableChart';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { AiChip } from './shared';

interface PendingPillProps {
  count: number;
  onClick: () => void;
}

const PendingPill = ({ count, onClick }: PendingPillProps) => {
  if (!count) return null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        bgcolor: (theme) => theme.palette.tokens.semantic.warningSurface,
        border: 1.5,
        borderColor: 'accent.main',
        color: (theme) => theme.palette.tokens.semantic.warningText,
        fontFamily: 'inherit',
        fontWeight: 600,
        fontSize: 13.5,
        px: 1.75,
        py: 0.85,
        borderRadius: 9999,
        transition: 'all .18s',
        '&:hover': { filter: 'brightness(0.97)' },
      }}
    >
      <Box
        component="span"
        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'accent.main' }}
      />
      {count} {count === 1 ? 'pendiente de revisar' : 'pendientes de revisar'}
      <ArrowForwardIcon sx={{ fontSize: 16 }} />
    </Box>
  );
};

interface ActionTileProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

const ActionTile = ({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
  disabledReason,
}: ActionTileProps) => {
  const tile = (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        bgcolor: 'background.paper',
        border: 1.5,
        borderColor: 'grey.100',
        borderRadius: '12px',
        px: 1.5,
        py: 1.25,
        transition: 'all .15s',
        '&:hover': disabled ? {} : { borderColor: 'primary.light', bgcolor: 'primary.50' },
        '&:hover .tile-arrow': disabled ? {} : { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '11px',
          bgcolor: 'primary.50',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: 700,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <ArrowForwardIcon
        className="tile-arrow"
        sx={{
          fontSize: 18,
          color: 'text.secondary',
          flexShrink: 0,
          opacity: 0,
          transform: 'translateX(-4px)',
          transition: 'all .15s',
        }}
      />
    </Box>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason}>
        <Box component="span" sx={{ display: 'block' }}>
          {tile}
        </Box>
      </Tooltip>
    );
  }

  return tile;
};

interface DataEntryBarProps {
  count: number;
  canImport: boolean;
  onUpload: () => void;
  onOpenInbox: () => void;
  onImport: () => void;
}

export const DataEntryBar = ({
  count,
  canImport,
  onUpload,
  onOpenInbox,
  onImport,
}: DataEntryBarProps) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      borderRadius: '16px',
      boxShadow: (theme) => theme.palette.tokens.shadows.card,
      border: 1,
      borderColor: 'grey.100',
      px: 2.5,
      py: 2,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: 1.75 }}
      flexWrap="wrap"
      useFlexGap
    >
      <BoltIcon sx={{ fontSize: 20, color: 'primary.main' }} />
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
        Registra tus gastos y movimientos
      </Typography>
      <AiChip />
      <Box sx={{ flex: 1 }} />
      <PendingPill count={count} onClick={onOpenInbox} />
    </Stack>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.5,
      }}
    >
      <ActionTile
        icon={<ReceiptLongIcon sx={{ fontSize: 19, color: 'primary.main' }} />}
        title="Subir factura"
        subtitle="La IA prepara el borrador. Tú confirmas."
        onClick={onUpload}
      />
      <ActionTile
        icon={<TableChartIcon sx={{ fontSize: 19, color: 'primary.main' }} />}
        title="Importar transacciones"
        subtitle="Desde un Excel o CSV de tu banco."
        onClick={onImport}
        disabled={!canImport}
        disabledReason="Primero registra una inversión para poder importar transacciones."
      />
    </Box>
  </Box>
);
